import { create } from 'zustand';
import { isRecycleBinNode, useRecycleStore } from '@/recycle/store';
import { resolve } from './resolve';
import { lookupParent, relocate } from './tree';
import type { FsFolder, FsNode } from './types';

/**
 * Moving files.
 *
 * The tree is built once from `src/config/filesystem.ts` and then genuinely
 * mutated by this module — dragging a file into a folder puts it *in* that
 * folder, so Explorer, the desktop, `DIR` and the address bar all agree about
 * where it now lives without any of them learning a second set of rules.
 *
 * Like the Recycle Bin, none of it is persisted. A reload rebuilds the disk
 * from config, which is this computer's undo for everything.
 */

interface FsStore {
  /** Bumped by every successful move. */
  version: number;
}

/**
 * `fs.list()` is a plain function call, so React has no way of knowing its
 * answer has changed. Anything that renders the contents of a folder reads
 * this counter as well, exactly as it already reads the Recycle Bin's mask.
 */
export const useFsStore = create<FsStore>(() => ({ version: 0 }));

export function useFsVersion(): number {
  return useFsStore((s) => s.version);
}

/* -------------------------------------------------------------------------- */
/* What may move, and where                                                   */
/* -------------------------------------------------------------------------- */

/** True for a special folder, or any folder holding one further down. */
function holdsSpecial(node: FsNode): boolean {
  if (node.type !== 'folder') return false;
  if (node.special) return true;
  return node.children.some(holdsSpecial);
}

/**
 * Whether this node can be picked up at all.
 *
 * Special folders stay put, and so does anything containing one. Half the
 * shell addresses them by literal path — `fs.roots.desktop` is a string
 * constant, and the desktop icons *are* whatever lives at it — so a Desktop
 * dragged into My Documents would take the icons with it and leave the shell
 * pointing at an address that no longer resolves.
 *
 * The Recycle Bin is exempt for the older reason: you could never file the bin
 * away inside a folder. It can still be slid around the desktop, which is
 * repositioning rather than moving.
 */
export function canMove(node: FsNode): boolean {
  if (isRecycleBinNode(node)) return false;
  return !holdsSpecial(node);
}

/**
 * Whether this can go in the Recycle Bin.
 *
 * The same rule as `canMove`, and for the same reason: a deleted path stops
 * resolving, so binning the Desktop would leave the shell holding an address
 * that answers to nothing. XP protected its own folders too — this is where
 * that protection lives.
 */
export function canDelete(node: FsNode): boolean {
  return canMove(node);
}

/** True when `dest` is `node` itself or somewhere inside it. */
function isWithin(dest: FsFolder, node: FsNode): boolean {
  if (dest === node) return true;
  const inside = `${node.path.toLowerCase()}\\`;
  return dest.path.toLowerCase().startsWith(inside);
}

/** Where a node lives now, by identity rather than by path. */
function parentOf(node: FsNode): FsFolder | undefined {
  return lookupParent(node.path.toLowerCase());
}

/**
 * Whether dropping `node` on `dest` would do anything. False for a folder
 * dropped into its own subtree, for the folder a node already sits in, and for
 * My Computer — which is a namespace root rather than a directory, and has
 * never held anything but drives.
 */
export function canAccept(dest: FsNode | undefined, node: FsNode): boolean {
  if (!dest || dest.type !== 'folder') return false;
  if (dest.special === 'my-computer') return false;
  if (!canMove(node)) return false;
  if (isWithin(dest, node)) return false;
  return parentOf(node) !== dest;
}

/**
 * A name already spoken for at the destination. Deleted files still hold
 * theirs: the Recycle Bin is a mask over a file that is still on the disk, and
 * restoring it must always find its old name free.
 */
function nameTaken(dest: FsFolder, node: FsNode): boolean {
  const name = node.name.toLowerCase();
  return dest.children.some((child) => child !== node && child.name.toLowerCase() === name);
}

/* -------------------------------------------------------------------------- */
/* The move itself                                                            */
/* -------------------------------------------------------------------------- */

export interface MoveReport {
  moved: FsNode[];
  /** Blocked by a file of the same name already at the destination. */
  conflicts: FsNode[];
  /** Blocked by the rules above: system folders, the bin, a folder into itself. */
  refused: FsNode[];
}

const EMPTY_REPORT: MoveReport = { moved: [], conflicts: [], refused: [] };

/**
 * Move as many of `nodes` into `destPath` as the rules allow, and report on
 * the rest. One version bump for the batch, so a ten-file drop repaints the
 * folders it touched exactly once.
 */
export function moveNodes(nodes: FsNode[], destPath: string): MoveReport {
  const dest = resolve(destPath);
  if (!dest || dest.type !== 'folder') return EMPTY_REPORT;

  const report: MoveReport = { moved: [], conflicts: [], refused: [] };

  for (const node of nodes) {
    if (!canAccept(dest, node)) {
      // Already in this folder is a no-op rather than a failure — dropping a
      // selection back where it came from should not raise anything.
      if (parentOf(node) !== dest) report.refused.push(node);
      continue;
    }
    if (nameTaken(dest, node)) {
      report.conflicts.push(node);
      continue;
    }
    const from = node.path;
    relocate(node, dest);
    // Anything under here that is in the Recycle Bin is masked by path, and
    // the path has just changed.
    useRecycleStore.getState().rekey(from, node.path);
    report.moved.push(node);
  }

  if (report.moved.length > 0) {
    useFsStore.setState((s) => ({ version: s.version + 1 }));
  }
  return report;
}
